import React, { useCallback, useState, useEffect, useMemo, ReactNode } from 'react';
import { PaginationParams } from '../../services/paginatedReportService';

interface VirtualizedReportTableProps<T> {
  // Table data and loading state
  data: T[];
  isLoading: boolean;
  
  // Table configuration
  columns: Array<{
    id: string;
    header: string;
    accessor: (item: T) => ReactNode;
    className?: string;
    headerClassName?: string;
  }>;
  
  // Virtualization configuration
  rowHeight?: number;
  headerHeight?: number;
  visibleRows?: number;
  
  // Pagination configuration
  pagination: PaginationParams;
  onPageChange: (page: number) => void;
  
  // Optional props
  emptyMessage?: string;
  loadingMessage?: string;
  className?: string;
  containerHeight?: number;
  enableVirtualization?: boolean;
  
  // Optional cell rendering customization
  renderCell?: (item: T, column: { id: string; header: string; accessor: (item: T) => ReactNode; className?: string; headerClassName?: string }) => ReactNode;
  
  // Sort configuration
  sortConfig?: { key: string; direction: 'asc' | 'desc' };
  onSort?: (key: string) => void;
}

/**
 * Virtualized table component for displaying large datasets
 * Uses windowing technique to only render visible rows for better performance
 */
function VirtualizedReportTable<T>({
  data,
  isLoading,
  columns,
  rowHeight = 48,
  headerHeight = 56,
  visibleRows = 10,
  pagination,
  onPageChange,
  emptyMessage = 'No data available',
  loadingMessage = 'Loading data...',
  className = '',
  containerHeight,
  enableVirtualization = true,
  renderCell,
  sortConfig,
  onSort
}: VirtualizedReportTableProps<T>) {
  // Calculate the actual visible height based on available data or specified visible rows
  const actualVisibleRows = useMemo(() => {
    return Math.min(visibleRows, data.length);
  }, [visibleRows, data.length]);
  
  // Calculate total height for container based on header and visible rows
  const calculatedHeight = useMemo(() => {
    // If containerHeight is explicitly set, use that
    if (containerHeight) return containerHeight;
    
    // When virtualizing, calculate based on visible rows
    if (enableVirtualization) {
      return headerHeight + (actualVisibleRows * rowHeight);
    }
    
    // Otherwise, calculate based on actual data length
    return headerHeight + (data.length * rowHeight);
  }, [containerHeight, enableVirtualization, headerHeight, rowHeight, actualVisibleRows, data.length]);
  
  // State for scroll position and visible range
  const [scrollTop, setScrollTop] = useState(0);
  
  // Calculate start and end indices for visible rows based on scroll position
  const { startIndex, endIndex } = useMemo(() => {
    if (!enableVirtualization) {
      return { startIndex: 0, endIndex: data.length - 1 };
    }
    
    const start = Math.floor(scrollTop / rowHeight);
    // Add buffer rows for smoother scrolling
    const bufferRows = 5;
    const end = Math.min(start + visibleRows + bufferRows, data.length - 1);
    
    return {
      startIndex: Math.max(0, start - bufferRows),
      endIndex: end
    };
  }, [scrollTop, rowHeight, visibleRows, data.length, enableVirtualization]);
  
  // Handle scrolling in the container
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);
  
  // Reset scroll position when data changes
  useEffect(() => {
    setScrollTop(0);
  }, [data]);
  
  // Generate pagination controls
  const renderPagination = () => {
    const { page, totalPages = 1 } = pagination;
    
    // Generate page buttons
    const pageButtons = [];
    
    // Always show first page
    pageButtons.push(
      <button
        key="first"
        className={`px-3 py-1 ${page === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'} rounded`}
        onClick={() => onPageChange(1)}
        disabled={page === 1}
      >
        1
      </button>
    );
    
    // Show ellipsis if needed
    if (page > 3) {
      pageButtons.push(
        <span key="ellipsis1" className="px-3 py-1">...</span>
      );
    }
    
    // Show pages around current page
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      if (i === 1 || i === totalPages) continue; // Skip first and last pages as they're handled separately
      
      pageButtons.push(
        <button
          key={i}
          className={`px-3 py-1 ${page === i ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'} rounded`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Show ellipsis if needed
    if (page < totalPages - 2) {
      pageButtons.push(
        <span key="ellipsis2" className="px-3 py-1">...</span>
      );
    }
    
    // Always show last page (if more than one page)
    if (totalPages > 1) {
      pageButtons.push(
        <button
          key="last"
          className={`px-3 py-1 ${page === totalPages ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'} rounded`}
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
        >
          {totalPages}
        </button>
      );
    }
    
    return (
      <div className="flex justify-between items-center mt-4 text-sm">
        <div>
          <span className="text-gray-600">
            Page {page} of {totalPages} • Showing {Math.min(pagination.pageSize, data.length)} of {pagination.totalItems} items
          </span>
        </div>
        
        <div className="flex space-x-1">
          <button
            className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>
          
          {pageButtons}
          
          <button
            className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };
  
  // Render a header cell with sort functionality
  const renderHeaderCell = (column: (typeof columns)[number], index: number) => {
    const isSortable = !!onSort;
    const isSorted = sortConfig?.key === column.id;
    const sortDirection = sortConfig?.direction;
    
    const handleClick = () => {
      if (isSortable && onSort) {
        onSort(column.id);
      }
    };
    
    return (
      <th
        key={column.id}
        className={`px-4 py-2 text-left ${column.headerClassName || ''} ${isSortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
        style={{ height: headerHeight }}
        onClick={isSortable ? handleClick : undefined}
      >
        <div className="flex items-center">
          <span>{column.header}</span>
          
          {isSortable && isSorted && (
            <span className="ml-1">
              {sortDirection === 'asc' ? '▲' : '▼'}
            </span>
          )}
        </div>
      </th>
    );
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
        <p className="text-gray-600">{emptyMessage}</p>
      </div>
    );
  }
  
  // Calculate the total content height for scrolling
  const totalContentHeight = data.length * rowHeight;
  
  // Visible data slice based on virtualization or full data if disabled
  const visibleData = enableVirtualization
    ? data.slice(startIndex, endIndex + 1)
    : data;
  
  return (
    <div className={`flex flex-col ${className}`}>
      <div 
        className="w-full overflow-auto border rounded"
        style={{ height: calculatedHeight }}
        onScroll={handleScroll}
      >
        <table className="min-w-full divide-y divide-gray-200 border-collapse">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {columns.map(renderHeaderCell)}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {/* If virtualized, add spacer to maintain scroll position */}
            {enableVirtualization && startIndex > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: startIndex * rowHeight }} />
              </tr>
            )}
            
            {/* Render only visible rows */}
            {visibleData.map((item, idx) => {
              const actualIndex = enableVirtualization ? startIndex + idx : idx;
              
              return (
                <tr 
                  key={actualIndex} 
                  className="hover:bg-gray-50 transition-colors"
                  style={{ height: rowHeight }}
                >
                  {columns.map((column) => (
                    <td 
                      key={`${actualIndex}-${column.id}`} 
                      className={`px-4 py-2 ${column.className || ''}`}
                    >
                      {renderCell 
                        ? renderCell(item, column) 
                        : column.accessor(item)}
                    </td>
                  ))}
                </tr>
              );
            })}
            
            {/* If virtualized, add spacer after visible rows */}
            {enableVirtualization && endIndex < data.length - 1 && (
              <tr>
                <td colSpan={columns.length} style={{ height: (data.length - endIndex - 1) * rowHeight }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination controls */}
      {pagination && pagination.totalPages && pagination.totalPages > 1 && renderPagination()}
    </div>
  );
}

export default VirtualizedReportTable;